import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AttachFilesActionButton from '../../../components/designe/ActionButtons/AttachFilesActionButton';
import SendActionButton from '../../../components/designe/ActionButtons/SendActionButton';
import AppFile from '../../../components/designe/app-file/AppFile';
import AppTextField from '../../../components/designe/textfields/app-text-field/AppTextField';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../../components/layout/page-container/page-container-error-handler';
import withRouter from '../../../hoc/with-router';
import { BugReportStatus } from '../../../shared/constants';
import { useStyles } from './BugReport.styles';
import BugReportMessage from './bug-report-message/BugReportMessage';
import { useBugReport } from './useBugReport';

function BugReport(props) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { params } = props;
  const {
    report,
    newPostFilesToUpload,
    newMessage,
    isLoading,
    isLoadingReport,
    isFetchingReport,
    isReportError,
    reportError,
    refetch,
    setMessage,
    onUploadNewPostFiles,
    onDeleteNewPostFile,
    onSendMessage,
  } = useBugReport(params.reportId);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'auto',
      });
    }
  }, [report]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSendMessage();
    }
  };
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  useRegisterPageContainerError({
    errors: isReportError && reportError ? [reportError] : [],
    isLoading: !report && (isLoadingReport || isFetchingReport),
    hasData: Boolean(report),
    onRetry: handleRetry,
  });

  return (
    <PageContainer backButton title={t('bugReport.bugReport')}>
      <Box className={classes.bugReportWrapper}>
        <Box className={classes.bugReportMessagesContainer} ref={containerRef}>
          {report?.messages?.map((message) => (
            <BugReportMessage message={message} key={message.id} />
          ))}
        </Box>
        {report?.status !== BugReportStatus.closed && (
          <Box className={classes.newMessageWrapper}>
            {!!newPostFilesToUpload.length && (
              <Box className={classes.uploadedFilesWrapper}>
                {newPostFilesToUpload.map((file) => (
                  <AppFile
                    key={file.id}
                    file={file}
                    onDelete={(file) => onDeleteNewPostFile(file.id)}
                  />
                ))}
              </Box>
            )}
            <Box className={classes.newMessageContainer}>
              <AppTextField
                label={t('bugReport.newMessage')}
                fullWidth
                multiline
                maxRows={5}
                value={newMessage}
                onKeyDown={handleKeyDown}
                onChange={(e) => setMessage(e.target.value)}
              />

              <AttachFilesActionButton
                onFileInput={(e) => onUploadNewPostFiles(e.target.files)}
              />
              <SendActionButton
                onClick={onSendMessage}
                disabled={
                  isLoading || (!newMessage && !newPostFilesToUpload.length)
                }
              />
            </Box>
          </Box>
        )}
      </Box>
    </PageContainer>
  );
}

export default withRouter(BugReport);
